import React, { useState, useCallback, useEffect } from 'react';
import { Button, ModalBody, ModalHeader } from 'reactstrap';
import { useDispatch } from 'react-redux';
import Rodal from '../Rodal/Rodal';
import BootstrapTable, {
    ColumnDescription,
} from '../react-bootstrap-table-next/react-bootstrap-table2';
import { UpdateVDRPermissions } from '../../slices/marketplace/data-room/thunks/update-vdr-permissions.thunk';
import {
    VDRAccessPermission,
    VDRAccessLevel,
    VDRAccessLevelList,
    SourceType,
} from '../../slices/marketplace/data-room/models/vdr-access-permission.model';
import ButtonLoader from '../Layout/Buttons/ButtonLoader';
import './DataRoomAccessRightsPopup.scss';

export interface DataRoomTeamMember {
    id: number;
    userId?: number;
    email: string;
    memberId: number;
    name: string;
    memberType: string;
    role: string;
}

interface DataRoomAccessRightsPopupProps {
    visible: boolean;
    folderPathLabel: string;
    folderPath: string;
    subFolders: string[];
    teamMembers: DataRoomTeamMember[];
    engagementId: number;
    permissions: VDRAccessPermission[];
    buyerUserId: number;
    onClose: () => void;
    onPermissionsUpdated?: () => void;
}

export const DataRoomAccessRightsPopup: React.FC<
    DataRoomAccessRightsPopupProps
> = ({
    visible,
    folderPathLabel,
    folderPath,
    subFolders,
    teamMembers,
    engagementId,
    permissions,
    buyerUserId,
    onClose,
    onPermissionsUpdated,
}) => {
    const dispatch = useDispatch() as any;
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [modifiedPermissions, setModifiedPermissions] =
        useState<VDRAccessPermission[]>(permissions);
    const [tableKey, setTableKey] = useState<number>(0);

    useEffect(() => {
        if (visible) {
            setModifiedPermissions(permissions);
            setTableKey((prev) => prev + 1);
        }
    }, [visible, permissions]);

    const getAccessLevelForMember = (email: string): VDRAccessLevel => {
        const permission = modifiedPermissions.find(
            (p) => p.email === email && p.routeUserAccess === folderPath
        );
        return permission
            ? permission.accessLevel
            : VDRAccessLevel.FolderAndSubfolders;
    };

    const handleAccessLevelChange = (
        email: string,
        newAccessLevel: VDRAccessLevel,
        userId?: number
    ) => {
        setModifiedPermissions((prevPermissions) => {
            const existingPermission = prevPermissions.find(
                (p) => p.email === email && p.routeUserAccess === folderPath
            );

            if (existingPermission) {
                return prevPermissions.map((p) =>
                    p.email === email && p.routeUserAccess === folderPath
                        ? { ...p, accessLevel: newAccessLevel }
                        : p
                );
            } else {
                const newPermission: VDRAccessPermission = {
                    id: 0,
                    email: email,
                    userId: userId,
                    sourceId: engagementId,
                    sourceType: SourceType.Engagement,
                    routeUserAccess: folderPath,
                    accessLevel: newAccessLevel,
                    grantedByUserId: 0,
                    ownerUserId: buyerUserId,
                };
                return [...prevPermissions, newPermission];
            }
        });
        setTableKey((prev) => prev + 1);
    };

    const accessFormatter = (_: any, row: DataRoomTeamMember) => {
        const currentAccessLevel = getAccessLevelForMember(row.email);
        return (
            <select
                className="form-control"
                value={currentAccessLevel}
                onChange={(e) =>
                    handleAccessLevelChange(
                        row.email,
                        Number(e.target.value) as VDRAccessLevel,
                        row.userId
                    )
                }>
                {VDRAccessLevelList.map((level) => (
                    <option key={level.id} value={level.id}>
                        {level.name}
                    </option>
                ))}
            </select>
        );
    };

    const handleUpdateAccessRights = useCallback(async () => {
        setIsUpdating(true);
        try {
            const completePermissions: VDRAccessPermission[] = teamMembers.map(
                (member) => {
                    const existingPermission = modifiedPermissions.find(
                        (p) =>
                            p.email === member.email &&
                            p.routeUserAccess === folderPath
                    );

                    if (existingPermission) {
                        return existingPermission;
                    }

                    return {
                        id: 0,
                        email: member.email,
                        userId: member.userId ?? 0,
                        sourceId: engagementId,
                        sourceType: SourceType.Engagement,
                        routeUserAccess: folderPath,
                        accessLevel: VDRAccessLevel.FolderAndSubfolders,
                        grantedByUserId: 0,
                        ownerUserId: buyerUserId,
                    };
                }
            );

            await dispatch(
                UpdateVDRPermissions({
                    permissions: completePermissions,
                })
            ).unwrap();
            
            if (onPermissionsUpdated) {
                onPermissionsUpdated();
            }
            
            onClose();
        } catch (error) {
            console.error('Failed to update access rights:', error);
        } finally {
            setIsUpdating(false);
        }
    }, [
        dispatch,
        engagementId,
        modifiedPermissions,
        onClose,
        onPermissionsUpdated,
        teamMembers,
        folderPath,
        buyerUserId,
    ]);

    const columns: ColumnDescription[] = [
        {
            dataField: 'name',
            text: 'Name',
            headerClasses: 'name-column-header',
        },
        {
            dataField: 'memberType',
            text: 'Type',
            headerClasses: 'type-column-header',
        },
        {
            dataField: 'role',
            text: 'Role',
            headerClasses: 'role-column-header',
        },
        {
            dataField: 'access',
            text: 'Access',
            headerStyle: { width: '250px' },
            headerClasses: 'access-column-header',
            formatter: accessFormatter,
        },
    ];

    return (
        <div className="data-room-access-rights-popup">
            <Rodal
                visible={visible}
                onClose={onClose}
                width={600}
                animation={'fade'}
                showMask={true}
                centered>
                <ModalHeader className="modal-title">
                    Team Access Rights
                </ModalHeader>
                <ModalBody>
                    <div className="folder-path">
                        <span className="folder-label">
                            <strong>Folder: </strong>
                        </span>
                        <span className="folder-value">{folderPathLabel}</span>
                    </div>
                    {subFolders.length > 0 && (
                        <div className="sub-folders">
                            <span className="sub-folders-label">
                                <strong>Sub Folders: </strong>
                            </span>
                            <span className="sub-folders-value">
                                {subFolders.join(', ')}
                            </span>
                        </div>
                    )}
                    <div className="table-wrapper">
                        <BootstrapTable
                            key={tableKey}
                            striped
                            bordered
                            bootstrap4
                            keyField="id"
                            data={teamMembers}
                            columns={columns}
                            noDataIndication="No team members available"
                        />
                    </div>
                    <div className="action-buttons">
                        <Button
                            color="secondary"
                            onClick={onClose}
                            disabled={isUpdating}
                            className="cancel-button">
                            Cancel
                        </Button>
                        <ButtonLoader
                            type="submit"
                            className={'btn btn-primary update-button'}
                            disabled={isUpdating}
                            isLoading={isUpdating}
                            loaderButtonText={''}
                            buttonText={'Update Access Rights'}
                            onClick={handleUpdateAccessRights}
                        />
                    </div>
                </ModalBody>
            </Rodal>
        </div>
    );
};
